/* Home hero carousel.

   - Rotates on a timer (data-interval ms). The active indicator pill fills
     over the same interval via CSS; `is-playing` starts the animation and
     re-adding the class restarts it on every slide change.
   - Pauses while hovered, focused, off-screen, or the tab is hidden, and
     never auto-rotates for users who prefer reduced motion.
   - Video slides play only while active; the next slide's video is nudged
     to preload so it has a first frame ready.
   - Swipe on touch, arrow keys when focused, theme-editor block select. */

class HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero-slide'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    if (!this.slides.length) return;

    this.index = 0;
    this.timer = null;
    this.interval = Number(this.dataset.interval) || 10000;
    this.autoplay = this.dataset.autoplay !== 'false' && this.slides.length > 1;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.hovered = false;
    this.focused = false;
    this.visible = true;
    this.editorHold = false;

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    // Touch browsers fire mouseenter on tap and never mouseleave, which
    // would stall the rotation; only honour hover on real pointer devices.
    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', () => { this.hovered = true; this.sync(); });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.sync(); });
    }
    this.addEventListener('focusin', () => { this.focused = true; this.sync(); });
    this.addEventListener('focusout', () => {
      this.focused = this.contains(document.activeElement) && document.activeElement !== this;
      this.sync();
    });
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', () => { this.swipeStart = null; });

    this.onVisibility = () => this.sync();
    document.addEventListener('visibilitychange', this.onVisibility);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      }, { threshold: 0.25 });
      this.observer.observe(this);
    }

    if (window.Shopify && window.Shopify.designMode) {
      this.onBlockSelect = (event) => {
        const slide = event.target.closest && event.target.closest('.hero-slide');
        if (!slide || !this.contains(slide)) return;
        this.editorHold = true;
        this.goTo(this.slides.indexOf(slide));
      };
      this.onBlockDeselect = () => { this.editorHold = false; this.sync(); };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    // Assign each video's source once, sized to the viewport; the markup
    // carries the URLs as data attributes so nothing downloads twice.
    const phone = window.innerWidth < 750;
    this.querySelectorAll('video[data-src-hd]').forEach((video) => {
      const src = (phone && video.dataset.srcSd) || video.dataset.srcHd;
      if (src && !video.getAttribute('src')) video.setAttribute('src', src);
    });

    this.render();
    this.sync();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.observer) this.observer.disconnect();
    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  /* ----- input ----- */

  onClick = (event) => {
    const dot = event.target.closest('[data-hero-dot]');
    if (dot) {
      this.goTo(Number(dot.dataset.heroDot));
      return;
    }
    if (event.target.closest('[data-hero-next]')) this.next();
    else if (event.target.closest('[data-hero-prev]')) this.prev();
  };

  onKeydown = (event) => {
    if (event.key === 'ArrowRight') { event.preventDefault(); this.next(); }
    else if (event.key === 'ArrowLeft') { event.preventDefault(); this.prev(); }
  };

  onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.swipeStart = { x: event.clientX, y: event.clientY };
  };

  onPointerUp = (event) => {
    if (!this.swipeStart) return;
    const dx = event.clientX - this.swipeStart.x;
    const dy = event.clientY - this.swipeStart.y;
    this.swipeStart = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) this.next();
    else this.prev();
  };

  /* ----- navigation ----- */

  next() { this.goTo((this.index + 1) % this.slides.length); }
  prev() { this.goTo((this.index - 1 + this.slides.length) % this.slides.length); }

  goTo(index) {
    if (index === this.index || index < 0 || index >= this.slides.length) {
      this.restart();
      return;
    }
    this.index = index;
    this.render();
    this.restart();
  }

  render() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');

      const video = slide.querySelector('video');
      if (!video) return;
      if (active) {
        video.preload = 'auto';
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      } else {
        video.pause();
        if (video.currentTime > 0) video.currentTime = 0;
      }
    });

    /* Warm the next slide's video so it has a frame ready */
    const upcoming = this.slides[(this.index + 1) % this.slides.length];
    const nextVideo = upcoming && upcoming.querySelector('video');
    if (nextVideo && nextVideo.preload !== 'auto') nextVideo.preload = 'auto';

    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    const current = this.slides[this.index];
    this.dataset.tone = current.classList.contains('hero-slide--dark') ? 'dark' : 'light';
  }

  /* ----- timer ----- */

  get shouldPlay() {
    return this.autoplay
      && !this.reduced
      && !this.hovered
      && !this.focused
      && !this.editorHold
      && this.visible
      && !document.hidden;
  }

  sync() {
    if (this.shouldPlay) {
      if (!this.timer) this.start();
      this.classList.remove('is-paused');
    } else if (this.timer) {
      this.pause();
    }
  }

  start() {
    this.stop();
    // Force a reflow so re-adding `is-playing` restarts the keyframes
    void this.offsetWidth;
    this.classList.add('is-playing');
    this.timer = setTimeout(() => {
      this.timer = null;
      this.next();
    }, this.interval);
  }

  /* Pause keeps the pill where it is; the timer is simply rebuilt from
     zero on resume, which is close enough for a 10 s rotation. */
  pause() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.classList.add('is-paused');
  }

  stop() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.classList.remove('is-playing', 'is-paused');
  }

  /* Restart the fill animation from zero on every slide change */
  restart() {
    this.stop();
    if (this.shouldPlay) this.start();
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
